// src/hooks/useNewsletter.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NewsletterService } from '../admin/pages/Newsletter/newsletterService';

export const NEWSLETTER_KEYS = {
  all: ['newsletter'] as const,
  lists: () => ['newsletter', 'list'] as const,
  paginated: (page: number, search: string) =>
    ['newsletter', 'list', page, search] as const,
  stats: () => ['newsletter', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface NewsletterPaginationParams {
  page: number;
  search: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getPaginated instead of cursor
// objects. Any page can now be fetched in any order.
export const useNewsletterPaginated = (params: NewsletterPaginationParams) => {
  return useQuery({
    queryKey: NEWSLETTER_KEYS.paginated(params.page, params.search),
    queryFn: () =>
      NewsletterService.getPaginated(
        params.pageSize,
        params.page,
        params.search || undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useNewsletterStats = () => {
  return useQuery({
    queryKey: NEWSLETTER_KEYS.stats(),
    queryFn: () => NewsletterService.getStats(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useNewsletterMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: NEWSLETTER_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (id: string) => NewsletterService.delete(id),
    onSuccess: invalidateAll,
  });

  const removeMany = useMutation({
    mutationFn: (ids: string[]) => NewsletterService.deleteMany(ids),
    onSuccess: invalidateAll,
  });

  return { remove, removeMany };
};